"use client";
import { useEffect, useState } from "react";
import useWebSocket from "react-use-websocket";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { Skeleton } from "@/app/admin/_common/ui/skeleton";
import GuestTableRow from "./GuestTableRow";
import { env } from "@/app/_utils/env";
import { Users } from "lucide-react";

const guestListWsUrl = `${env.wsBaseUrl}ws/show/guestlist/`;

export default function GuestTable({
  guests,
  show,
  showId,
  futureShows,
  liveCheckIn = true,
  showColumn = false,
  setCheckInCount,
  cachedGuests,
  setCachedGuests,
  onChanged,
  containerClassName,
  isLoading = false,
}: {
  guests: any[];
  show?: any;
  showId?: string;
  futureShows?: any[];
  liveCheckIn?: boolean;
  showColumn?: boolean;
  setCheckInCount?: (count: number) => void;
  cachedGuests?: any[];
  setCachedGuests?: (guests: any[]) => void;
  onChanged?: () => void;
  containerClassName?: string;
  isLoading?: boolean;
}) {
  const [guestState, setGuestState] = useState<Record<string, any>>({});
  const { lastMessage, sendMessage } = useWebSocket(
    liveCheckIn ? guestListWsUrl : null,
    {
      reconnectInterval: 3000,
      reconnectAttempts: 5000,
      retryOnError: true,
      shouldReconnect: () => true,
    },
    liveCheckIn
  );

  useEffect(() => {
    if (!liveCheckIn || lastMessage === null) return;
    const message = JSON.parse(lastMessage.data);
    if (message.type === "list-check") {
      setGuestState((prev) => ({ ...prev, [message.data.id]: message.data }));
      if (setCachedGuests && cachedGuests) {
        const dict: Record<string, any> = {};
        cachedGuests.forEach((g) => (dict[g.id] = g));
        dict[message.data.id] = message.data;
        const nextGuests = Object.values(dict);
        setCachedGuests(nextGuests);
        if (setCheckInCount) {
          setCheckInCount(nextGuests.reduce((sum, g) => sum + Number(g.redeemed || 0), 0));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  const sendclient = (item: string) => {
    if (liveCheckIn) sendMessage(item);
  };

  const resolveOrder = (order: any) => guestState[order.id] ?? order;

  return (
    <div className={containerClassName ?? "flex h-full flex-col overflow-hidden rounded-md border border-border bg-card"}>
      <Table containerClassName="min-h-0 flex-1 overflow-y-auto" className="table-fixed">
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-full text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-10 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" />
            {showColumn && (
              <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Show
              </TableHead>
            )}
            <TableHead className="hidden h-8 w-[22%] text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">
              Email
            </TableHead>
            <TableHead className="hidden h-8 w-[15%] text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:table-cell">
              Instagram
            </TableHead>
            {liveCheckIn && (
              <TableHead className="h-8 w-12 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" />
            )}
            <TableHead className="h-8 w-16 pr-8 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 18 }).map((_, i) => (
              <TableRow key={i} className="hover:bg-transparent">
                <TableCell className="py-3">
                  <div className="flex flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-28" />
                    <div className="flex flex-col gap-1 md:hidden">
                      <Skeleton className="h-3 w-32" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <Skeleton className="h-3.5 w-8" />
                </TableCell>
                {showColumn && (
                  <TableCell className="py-3">
                    <Skeleton className="h-3.5 w-20" />
                  </TableCell>
                )}
                <TableCell className="hidden py-3 md:table-cell">
                  <Skeleton className="ml-auto h-3.5 w-32" />
                </TableCell>
                <TableCell className="hidden py-3 md:table-cell">
                  <Skeleton className="ml-auto h-3.5 w-20" />
                </TableCell>
                {liveCheckIn && (
                  <TableCell className="py-3">
                    <Skeleton className="mx-auto h-3.5 w-10" />
                  </TableCell>
                )}
                <TableCell className="py-3 pr-8">
                  <Skeleton className="ml-auto size-4 rounded" />
                </TableCell>
              </TableRow>
            ))
          ) : !Array.isArray(guests) || guests.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={5 + (showColumn ? 1 : 0) + (liveCheckIn ? 1 : 0)}
                className="h-[240px] p-0"
              >
                <CuoEmpty icon={Users} text="No guests" />
              </TableCell>
            </TableRow>
          ) : (
            guests.map((order) => (
              <GuestTableRow
                key={order.id}
                order={resolveOrder(order)}
                show={show}
                showId={showId}
                futureShows={futureShows}
                showColumn={showColumn}
                liveCheckIn={liveCheckIn}
                sendclient={sendclient}
                onChanged={onChanged}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
